import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Upload, DownloadSimple, ArrowLeft, FileXls, Check, X, Warning,
  ArrowsClockwise, Trash, Eye, CloudArrowUp
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';

const API = process.env.REACT_APP_BACKEND_URL;
const MANDATORY = ['Seller SKU', 'Product Name', 'Warehouse', 'Available Quantity'];
const OPTIONAL = ['Reserved Quantity', 'Safety Stock', 'Reorder Threshold', 'Inventory Status', 'Batch Number', 'Expiry Date', 'Bin Location'];

export default function BulkUploadPage() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [errors, setErrors] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [step, setStep] = useState('upload'); // upload, preview, result
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const downloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([
      [...MANDATORY, ...OPTIONAL],
      ['SKU-001', 'Blue T-Shirt L', 'Mumbai Warehouse', 100, '', 10, 20, '', '', '', ''],
      ['SKU-002', 'Red Sneakers 42', 'Delhi Warehouse', 50, '', 5, 15, '', '', '', ''],
      ['SKU-003', 'Cotton Bedsheet', 'Mumbai Warehouse', 200, '', 20, 50, '', '', '', ''],
    ]);
    // Column widths
    ws['!cols'] = MANDATORY.concat(OPTIONAL).map(() => ({ wch: 18 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Inventory Upload');
    XLSX.writeFile(wb, 'inventory_upload_template.xlsx');
    toast.success('Template downloaded');
  };

  const parseFile = useCallback((f) => {
    setFile(f);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(ws, { defval: '' });
        if (data.length === 0) { toast.error('File is empty'); return; }

        // Validate headers
        const headers = Object.keys(data[0]);
        const missingMandatory = MANDATORY.filter(m => !headers.some(h => h.trim().toLowerCase() === m.toLowerCase()));
        if (missingMandatory.length > 0) {
          toast.error(`Missing mandatory columns: ${missingMandatory.join(', ')}`);
          return;
        }

        // Normalize keys
        const normalizedRows = data.map(row => {
          const n = {};
          for (const [k, v] of Object.entries(row)) {
            const match = [...MANDATORY, ...OPTIONAL].find(c => c.toLowerCase() === k.trim().toLowerCase());
            n[match || k] = v;
          }
          return n;
        });

        // Client-side validation
        const errs = [];
        normalizedRows.forEach((row, i) => {
          const rowErrors = [];
          if (!row['Seller SKU']) rowErrors.push('SKU is required');
          if (!row['Warehouse']) rowErrors.push('Warehouse is required');
          const qty = row['Available Quantity'];
          if (qty === '' || qty === undefined || qty === null) rowErrors.push('Quantity is required');
          else if (isNaN(Number(qty)) || Number(qty) < 0) rowErrors.push(`Invalid quantity: ${qty}`);
          if (rowErrors.length) errs.push({ row: i + 2, sku: row['Seller SKU'], errors: rowErrors });
        });

        setRows(normalizedRows);
        setErrors(errs);
        setStep('preview');
      } catch (err) {
        toast.error('Failed to parse file. Ensure it is a valid XLSX or CSV.');
      }
    };
    reader.readAsArrayBuffer(f);
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer?.files?.[0];
    if (f && (f.name.endsWith('.xlsx') || f.name.endsWith('.csv'))) parseFile(f);
    else toast.error('Only .xlsx and .csv files supported');
  }, [parseFile]);

  const onFileSelect = (e) => {
    const f = e.target.files?.[0];
    if (f) parseFile(f);
  };

  const handleUpload = async () => {
    setUploading(true);
    try {
      const { data } = await axios.post(`${API}/api/inventory/bulk-upload`, { rows, action: 'update' }, { withCredentials: true });
      setResult(data);
      setStep('result');
      if (data.failed === 0) toast.success(`All ${data.success} rows processed!`);
      else toast.warning(`${data.success} succeeded, ${data.failed} failed`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const downloadErrors = () => {
    if (!result?.errors?.length) return;
    const ws = XLSX.utils.json_to_sheet(result.errors.map(e => ({ Row: e.row, SKU: e.sku, Errors: e.errors.join('; ') })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Errors');
    XLSX.writeFile(wb, 'upload_errors.xlsx');
  };

  const fetchHistory = async () => {
    try {
      const { data } = await axios.get(`${API}/api/inventory/uploads?limit=10`, { withCredentials: true });
      setHistory(data.uploads || []);
      setShowHistory(true);
    } catch { toast.error('Failed to load history'); }
  };

  const reset = () => { setFile(null); setRows([]); setErrors([]); setResult(null); setStep('upload'); };

  const validRows = rows.length - errors.length;

  return (
    <DashboardLayout
      title="Bulk Inventory Upload"
      subtitle="Upload Excel/CSV to update inventory across warehouses"
      actions={<div className="flex gap-2">
        <Button variant="outline" onClick={() => navigate('/inventory')} data-testid="back-to-inventory"><ArrowLeft size={16} className="mr-1.5" /> Back</Button>
        <Button variant="outline" onClick={fetchHistory} data-testid="upload-history-btn"><ArrowsClockwise size={16} className="mr-1.5" /> History</Button>
      </div>}
    >
      <div data-testid="bulk-upload-page">
        {step === 'upload' && (
          <div className="space-y-6">
            {/* Instructions */}
            <div className="bg-[#f9f9fb] border border-[#e5e5ea] rounded-xl p-5">
              <h3 className="text-sm font-bold text-[#0A0A0A] mb-3">How it works</h3>
              <ol className="text-xs text-[#6b6b8a] space-y-1.5 list-decimal list-inside">
                <li>Download the template file with the correct column headers</li>
                <li>Fill in your inventory data — Seller SKU must match existing products</li>
                <li>Warehouse name must match an existing warehouse in the system</li>
                <li>Upload the file and preview the data before submitting</li>
                <li>Review any validation errors and fix them before final submission</li>
              </ol>
              <Button variant="outline" onClick={downloadTemplate} className="mt-4 text-sm" data-testid="download-template-btn"><DownloadSimple size={16} className="mr-1.5" /> Download Template</Button>
            </div>

            {/* Drop zone */}
            <div
              onDragOver={e => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
              onClick={() => fileRef.current?.click()}
              className={`border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-all ${dragging ? 'border-[#7d64ff] bg-[#7d64ff]/5' : 'border-[#e5e5ea] hover:border-[#7d64ff]/40 hover:bg-[#fafafe]'}`}
              data-testid="drop-zone"
            >
              <CloudArrowUp size={48} className={`mx-auto mb-4 ${dragging ? 'text-[#7d64ff]' : 'text-[#9c9db7]'}`} />
              <p className="text-sm font-semibold text-[#0A0A0A]">Drop your file here or click to browse</p>
              <p className="text-xs text-[#9c9db7] mt-1">Supports .xlsx and .csv files</p>
              <input ref={fileRef} type="file" accept=".xlsx,.csv" onChange={onFileSelect} className="hidden" data-testid="file-input" />
            </div>
          </div>
        )}

        {step === 'preview' && (
          <div className="space-y-4">
            {/* Summary */}
            <div className="flex items-center justify-between bg-white border border-[#e5e5ea] rounded-xl px-5 py-4">
              <div className="flex items-center gap-4">
                <FileXls size={32} className="text-green-600" />
                <div>
                  <p className="text-sm font-semibold text-[#0A0A0A]">{file?.name}</p>
                  <p className="text-xs text-[#9c9db7]">{rows.length} rows &middot; {validRows} valid &middot; {errors.length} errors</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={reset}><Trash size={14} className="mr-1" /> Clear</Button>
                <Button onClick={handleUpload} disabled={uploading || validRows === 0} className="bg-[#7d64ff] hover:bg-[#6b4fef] text-white" data-testid="submit-upload-btn">
                  <Upload size={16} className="mr-1.5" /> {uploading ? 'Uploading...' : `Upload ${validRows} Rows`}
                </Button>
              </div>
            </div>

            {/* Errors */}
            {errors.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <p className="text-sm font-semibold text-red-700 mb-2"><Warning size={16} className="inline mr-1" />{errors.length} rows have errors (will be skipped)</p>
                <div className="max-h-[150px] overflow-y-auto space-y-1">
                  {errors.map((e, i) => <p key={i} className="text-xs text-red-600">Row {e.row} ({e.sku}): {e.errors.join(', ')}</p>)}
                </div>
              </div>
            )}

            {/* Preview table */}
            <div className="bg-white border border-[#e5e5ea] rounded-xl overflow-hidden">
              <div className="overflow-x-auto max-h-[400px]">
                <table className="w-full text-xs" data-testid="preview-table">
                  <thead className="bg-[#f9f9fb] border-b border-[#e5e5ea] sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold text-[#9c9db7]">#</th>
                      {MANDATORY.concat(OPTIONAL.filter(o => rows.some(r => r[o] !== '' && r[o] !== undefined))).map(col => (
                        <th key={col} className="px-3 py-2 text-left font-semibold text-[#9c9db7]">{col}</th>
                      ))}
                      <th className="px-3 py-2 text-left font-semibold text-[#9c9db7]">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.slice(0, 100).map((row, idx) => {
                      const rowErr = errors.find(e => e.row === idx + 2);
                      return (
                        <tr key={idx} className={`border-b border-[#f0f0f0] ${rowErr ? 'bg-red-50' : 'hover:bg-[#fafafe]'}`}>
                          <td className="px-3 py-2 text-[#9c9db7]">{idx + 2}</td>
                          {MANDATORY.concat(OPTIONAL.filter(o => rows.some(r => r[o] !== '' && r[o] !== undefined))).map(col => (
                            <td key={col} className="px-3 py-2">{row[col]}</td>
                          ))}
                          <td className="px-3 py-2">{rowErr ? <span className="text-red-600"><X size={12} className="inline" /> Error</span> : <span className="text-green-600"><Check size={12} className="inline" /> Valid</span>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {rows.length > 100 && <p className="text-xs text-[#9c9db7] text-center py-2">Showing first 100 of {rows.length} rows</p>}
            </div>
          </div>
        )}

        {step === 'result' && result && (
          <div className="space-y-5">
            <div className={`rounded-2xl p-8 text-center ${result.failed === 0 ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
              {result.failed === 0 ? <Check size={48} weight="bold" className="mx-auto text-green-600 mb-3" /> : <Warning size={48} className="mx-auto text-amber-600 mb-3" />}
              <h2 className="text-xl font-bold">{result.failed === 0 ? 'Upload Successful!' : 'Upload Completed with Errors'}</h2>
              <p className="text-sm text-[#6b6b8a] mt-2">{result.success} rows processed &middot; {result.failed} failed</p>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white border border-green-200 rounded-xl p-4 text-center"><p className="text-[10px] font-semibold text-green-600 uppercase">Success</p><p className="text-2xl font-bold text-green-700">{result.success}</p></div>
              <div className="bg-white border border-red-200 rounded-xl p-4 text-center"><p className="text-[10px] font-semibold text-red-600 uppercase">Failed</p><p className="text-2xl font-bold text-red-700">{result.failed}</p></div>
              <div className="bg-white border border-[#e5e5ea] rounded-xl p-4 text-center"><p className="text-[10px] font-semibold text-[#9c9db7] uppercase">Total</p><p className="text-2xl font-bold">{result.success + result.failed}</p></div>
            </div>

            {result.errors?.length > 0 && (
              <div className="bg-white border border-[#e5e5ea] rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-[#0A0A0A]">Error Details</h3>
                  <Button variant="outline" size="sm" onClick={downloadErrors} data-testid="download-errors-btn"><DownloadSimple size={14} className="mr-1" /> Download Errors</Button>
                </div>
                <div className="max-h-[200px] overflow-y-auto space-y-1">
                  {result.errors.map((e, i) => <p key={i} className="text-xs text-red-600">Row {e.row} ({e.sku}): {e.errors.join(', ')}</p>)}
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <Button onClick={reset} variant="outline" className="flex-1"><Upload size={16} className="mr-1.5" /> Upload Another</Button>
              <Button onClick={() => navigate('/inventory')} className="flex-1 bg-[#7d64ff] hover:bg-[#6b4fef] text-white">Back to Inventory</Button>
            </div>
          </div>
        )}

        {/* Upload History Dialog */}
        {showHistory && (
          <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center" onClick={() => setShowHistory(false)}>
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[70vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()} data-testid="upload-history-panel">
              <h2 className="text-lg font-bold text-[#0A0A0A] mb-4">Upload History</h2>
              {history.length === 0 ? <p className="text-sm text-[#9c9db7]">No uploads yet</p> : (
                <div className="space-y-2">{history.map(h => (
                  <div key={h.upload_id} className="flex items-center justify-between bg-[#f9f9fb] rounded-lg px-4 py-3">
                    <div>
                      <p className="text-sm font-medium">{h.total_rows} rows &middot; <span className="text-green-600">{h.success_count} ok</span> &middot; <span className="text-red-500">{h.failed_count} failed</span></p>
                      <p className="text-[10px] text-[#9c9db7]">{new Date(h.created_at).toLocaleString()} &middot; {h.upload_id}</p>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${h.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{h.status}</span>
                  </div>
                ))}</div>
              )}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
